import { APP_BASE_HREF, CommonModule, ViewportScroller, isPlatformBrowser, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule, PLATFORM_ID, isDevMode } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { HeaderModule } from '@commons/header/header.component';
import { LogoModule } from '@commons/logo/logo.component';
import { NotificationsModule } from '@commons/notifications/notifications.module';
import { SharedModule } from '@commons/shared.module';
import { TrustBoxWidgetComponent } from '@commons/trust-box-widget/trust-box-widget.component';
import { bugsnagConfig } from '@config/bugsnag.config';
import { CoreComponent } from '@core/components/core/core.component';
import { ngxsConfig } from '@core/ngxs.config';
import { AppBrowserViewportScroller } from '@core/scroller/app-browser-viewport-scroller';
import { WizbiiRouterStateSerializer } from '@core/serializer/router.serializer';
import { JwtService } from '@core/services/jwt.service';
import { environment } from '@environment';
import { FeaturesModule } from '@features/features.module';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule, RouterStateSerializer } from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { ConsentsState } from '@stores/consents/consents.state';
import { ServicesState } from '@stores/services/services.state';
import { SessionState } from '@stores/session/session.state';
import { JWT_SERVICE_TOKEN, JwtInterceptorFn } from '@wizbii-utils/angular/jwt';
import { ACCOUNT_API_CONFIG, AccountWebservice } from '@wizbii-utils/angular/webservices';
import { DataStorageService } from '@wizbii/angular-utilities';
import { CookieService } from 'ngx-cookie-service';
import { QuicklinkModule, QuicklinkStrategy } from 'ngx-quicklink';

Bugsnag.start(bugsnagConfig);

@NgModule({
  imports: [QuicklinkModule],
  exports: [QuicklinkModule],
})
export class QuicklinkSharedModule {}

@NgModule({
  declarations: [CoreComponent],
  bootstrap: [CoreComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    HammerModule,
    SharedModule.forRoot(),
    MatIconModule,
    QuicklinkSharedModule,
    RouterModule.forRoot([], {
      initialNavigation: 'enabledBlocking',
      onSameUrlNavigation: 'reload',
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      preloadingStrategy: QuicklinkStrategy,
    }),
    NgxsModule.forRoot([SessionState, ConsentsState, ServicesState], ngxsConfig),
    NgxsRouterPluginModule.forRoot(),
    ScrollToModule.forRoot(),
    MatProgressBarModule,
    FeaturesModule,
    HeaderModule,
    NotificationsModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot({ name: 'WIZBII Account', maxAge: 30 }),
    StoreModule.forRoot(),
    EffectsModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: false, // If set to true, the connection is established within the Angular zone
      name: 'account-ngrx',
    }),
    LogoModule,
    TrustBoxWidgetComponent,
    TrustBoxWidgetComponent,
  ],
  providers: [
    DataStorageService,
    CookieService,
    {
      provide: ErrorHandler,
      useFactory: () => new BugsnagErrorHandler(),
    },
    { provide: RouterStateSerializer, useClass: WizbiiRouterStateSerializer },
    { provide: ViewportScroller, useClass: AppBrowserViewportScroller },
    { provide: LOCALE_ID, useValue: environment.lang },
    { provide: APP_BASE_HREF, useValue: '/account/' },
    { provide: JWT_SERVICE_TOKEN, useClass: JwtService },
    AccountWebservice,
    {
      provide: ACCOUNT_API_CONFIG,
      useFactory: (platformId: any) => ({
        appId: environment.applicationId,
        baseUrl: isPlatformBrowser(platformId) ? '/_api/account-api' : 'http://account-api',
        locale: environment.locale,
        cookieDomains: environment.domainsByLocale,
        platform: environment.platform,
        defaultCookieDomain: environment.platform === 'local' ? 'localhost' : undefined,
      }),
      deps: [PLATFORM_ID],
    },
    provideHttpClient(withInterceptors([JwtInterceptorFn])),
  ],
})
export class CoreModule {
  constructor() {
    this.registerLocale();
  }

  async registerLocale() {
    try {
      // register locale for pipes
      const localeModule = await import(
        /* webpackInclude: /\b(en|fr|de|it|es)\.js/ */
        /* webpackMode: "lazy-once" */
        /* webpackChunkName: "i18n-base" */
        `/node_modules/@angular/common/locales/${environment.lang}.js`
      );
      registerLocaleData(localeModule.default);
    } catch {
      console.warn('Translation file error found for Angular pipes.');
    }
  }
}
